import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../api/apiClient";

export function usePrefersReducedMotion() {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/** Animates a number from its previous value to `target` (ease-out). */
export function useCountUp(target, { duration = 900 } = {}) {
  const reduced = usePrefersReducedMotion();
  const [value, setValue] = useState(Number(target) || 0);
  const from = useRef(0);
  useEffect(() => {
    const to = Number(target) || 0;
    if (reduced || !Number.isFinite(to)) {
      setValue(to);
      from.current = to;
      return undefined;
    }
    const start = performance.now(), origin = from.current;
    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setValue(origin + (to - origin) * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
      else from.current = to;
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      from.current = to;
    };
  }, [target, duration, reduced]);
  return value;
}

/**
 * Loads a dashboard activity endpoint for a range. While a new range loads,
 * the previous data stays (`refreshing`) so charts keep their frame.
 */
export function useActivity(path, range, { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, loading: enabled, refreshing: false, error: null });
  const request = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) return;
    const id = ++request.current;
    setState((s) => ({ ...s, loading: !s.data, refreshing: !!s.data, error: null }));
    const result = await apiClient.get(path, { range });
    if (id !== request.current) return;
    if (result.ok) setState({ data: result.data, loading: false, refreshing: false, error: null });
    else setState((s) => ({ ...s, loading: false, refreshing: false, error: "We couldn't load your activity." }));
  }, [path, range, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}

/** Builds chart rows from an activity payload: [{ bucket, label, title, ...series }]. */
export function toRows(data, labelFn, titleFn) {
  if (!data?.buckets) return [];
  return data.buckets.map((bucket, i) => {
    const row = { bucket, label: labelFn(bucket, data.granularity), title: titleFn(bucket, data.granularity) };
    for (const [key, values] of Object.entries(data.series || {})) row[key] = values[i] ?? 0;
    return row;
  });
}
